import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabaseClient';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';
import { auth$ } from './auth$';
import { trackEvent, setOnceUserProperties, incrementUserProperties } from '../lib/analytics';
import { Capacitor } from '@capacitor/core';

/** Detect source platform for internal analytics */
const getSource = () => Capacitor.isNativePlatform() ? 'android' : 'web';

// Snippets store with Supabase sync (local-first)
// Using syncedSupabase for automatic sync with local persistence
// NOTE: RLS handles permissions - user can only see own snippets
export const snippets$ = observable(syncedSupabase({
  supabase,
  collection: 'snippets',
  // RLS handles the filtering - user sees only own snippets
  // Sync ALL snippets (including deleted) for trash view
  // Filtering of deleted snippets happens at UI level (like archived notes)
  filter: (select) =>
    select
      .order('created_at', { ascending: true }),
  // Custom create/update with error handling
  create: createSyncCreate('snippets'),
  update: createSyncUpdate('snippets'),
  // Use default sync config but WITHOUT fieldDeleted
  // We manage 'deleted' as a regular boolean for trash view functionality
  // (fieldDeleted would cause Legend-State to remove deleted records from local state)
  ...defaultSyncConfig,
  fieldDeleted: undefined, // Override to keep deleted snippets in local state for trash view
  // IMPORTANT: Wait for auth before syncing
  // This prevents sync from failing when user logs in on a new device
  // Without this, sync tries to fetch before auth is ready and RLS blocks it
  waitFor: () => auth$.isAuthenticated.get(),
  // Data shape: object keyed by id (default, but explicit for clarity)
  as: 'object',
  // Initial empty object to prevent undefined issues
  initial: {},
  // Persist data locally
  persist: {
    name: 'promptnote-snippets',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
}));

// Helper: Get snippets for a note (active only)
export const getSnippetsForNote = (noteId) => {
  const snippetsObj = snippets$.get();
  return Object.values(snippetsObj)
    .filter(snippet => snippet && snippet.note_id === noteId && !snippet.deleted)
    .sort((a, b) => a.version - b.version);
};

// Helper: Get all snippets for a note (including deleted)
export const getAllSnippetsForNote = (noteId) => {
  const snippetsObj = snippets$.get();
  return Object.values(snippetsObj)
    .filter(snippet => snippet && snippet.note_id === noteId)
    .sort((a, b) => a.version - b.version);
};

// Helper: Get deleted snippets (for trash view)
export const getDeletedSnippets = () => {
  const snippetsObj = snippets$.get();
  return Object.values(snippetsObj)
    .filter(snippet => snippet && snippet.deleted)
    .sort((a, b) => new Date(b.deleted_at) - new Date(a.deleted_at));
};

// Helper: Get snippet filter counts for a note
export const getSnippetFilterCounts = (noteId) => {
  const snippets = getSnippetsForNote(noteId);
  return {
    upvoted: snippets.filter(s => s.upvotes > 0).length,
    favorited: snippets.filter(s => s.favorite).length,
  };
};

// Helper: Get next version number for a note
const getNextVersion = (noteId) => {
  const noteSnippets = getSnippetsForNote(noteId);
  if (noteSnippets.length === 0) return 1;
  return Math.max(...noteSnippets.map(s => s.version)) + 1;
};

// Create a new snippet
// Note: Pass userId and updateNoteTimestamp callback to avoid circular dependency
// Optional: images array (URLs) and custom snippetId (for pre-generated IDs when uploading images)
export const createSnippet = (noteId, content, title = '', userId, updateNoteTimestamp, images = [], customSnippetId = null) => {
  if (!userId) {
    console.error('[Snippets] Cannot create snippet: userId required');
    return null;
  }

  const snippetId = customSnippetId || uuidv4();
  const version = getNextVersion(noteId);

  const newSnippet = {
    id: snippetId,
    note_id: noteId,
    version,
    content,
    title,
    favorite: false,
    upvotes: 0,
    downvotes: 0,
    preview_mode: false,
    user_id: userId,
    created_at: new Date().toISOString(),
    images: images || [],
    source: getSource(),
  };

  snippets$[snippetId].set(newSnippet);

  // Update note's updated_at if callback provided
  if (updateNoteTimestamp) {
    updateNoteTimestamp(noteId);
  }

  // Analytics
  trackEvent('snippet_created', {
    version,
    has_images: images.length > 0,
    content_length: content?.length || 0,
  });
  setOnceUserProperties({ first_snippet_created_at: new Date().toISOString() });
  incrementUserProperties({ total_snippets_created: 1 });

  console.log('[Snippets] Created snippet:', snippetId, 'version:', version, 'images:', images.length);
  return newSnippet;
};

// Update a snippet
// Note: Pass updateNoteTimestamp callback to avoid circular dependency
export const updateSnippet = (snippetId, updates, updateNoteTimestamp) => {
  const snippet = snippets$[snippetId].get();
  if (!snippet) {
    console.error('[Snippets] Snippet not found:', snippetId);
    return;
  }

  snippets$[snippetId].set({
    ...snippet,
    ...updates,
  });

  // Update note's updated_at if callback provided
  if (updateNoteTimestamp) {
    updateNoteTimestamp(snippet.note_id);
  }

  console.log('[Snippets] Updated snippet:', snippetId);
};

// Soft delete a snippet (moves to trash)
// Note: Pass updateNoteTimestamp callback to avoid circular dependency
export const deleteSnippet = (snippetId, updateNoteTimestamp) => {
  const snippet = snippets$[snippetId].get();
  if (!snippet) {
    console.error('[Snippets] Snippet not found:', snippetId);
    return;
  }

  const noteId = snippet.note_id;

  // Soft delete: set deleted flag and timestamp
  snippets$[snippetId].set({
    ...snippet,
    deleted: true,
    deleted_at: new Date().toISOString(),
  });

  // Update note's updated_at if callback provided
  if (updateNoteTimestamp) {
    updateNoteTimestamp(noteId);
  }

  console.log('[Snippets] Soft deleted snippet:', snippetId);
};

// Restore a deleted snippet
// Note: Pass updateNoteTimestamp callback to avoid circular dependency
export const restoreSnippet = (snippetId, updateNoteTimestamp) => {
  const snippet = snippets$[snippetId].get();
  if (!snippet) {
    console.error('[Snippets] Snippet not found:', snippetId);
    return;
  }

  const noteId = snippet.note_id;

  // Restore: clear deleted flag and timestamp
  snippets$[snippetId].set({
    ...snippet,
    deleted: false,
    deleted_at: null,
  });

  // Update note's updated_at if callback provided
  if (updateNoteTimestamp) {
    updateNoteTimestamp(noteId);
  }

  console.log('[Snippets] Restored snippet:', snippetId);
};

// Permanently delete a snippet (irreversible)
// Note: Pass updateNoteTimestamp callback to avoid circular dependency
export const permanentlyDeleteSnippet = (snippetId, updateNoteTimestamp) => {
  const snippet = snippets$[snippetId].get();
  if (!snippet) {
    console.error('[Snippets] Snippet not found:', snippetId);
    return;
  }

  const noteId = snippet.note_id;

  // Hard delete from database
  snippets$[snippetId].delete();

  // Update note's updated_at if callback provided
  if (updateNoteTimestamp) {
    updateNoteTimestamp(noteId);
  }

  console.log('[Snippets] Permanently deleted snippet:', snippetId);
};

// Toggle preview_mode (persisted to DB for edit-permission users)
export const togglePreviewMode = (snippetId) => {
  const snippet = snippets$[snippetId].get();
  if (!snippet) return;

  snippets$[snippetId].preview_mode.set(!snippet.preview_mode);
};

// Toggle favorite
export const toggleFavorite = (snippetId) => {
  const snippet = snippets$[snippetId].get();
  if (!snippet) return;

  const newState = !snippet.favorite;
  snippets$[snippetId].favorite.set(newState);

  if (newState) {
    trackEvent('snippet_favorited', { snippet_id: snippetId });
  }
};

// Upvote snippet
export const upvoteSnippet = (snippetId) => {
  const snippet = snippets$[snippetId].get();
  if (!snippet) return;

  const wasUpvoted = snippet.upvotes > 0;
  snippets$[snippetId].upvotes.set(wasUpvoted ? 0 : 1);
  if (!wasUpvoted) {
    snippets$[snippetId].downvotes.set(0);
    trackEvent('snippet_voted', { direction: 'up', snippet_id: snippetId });
  }
};

// Downvote snippet
export const downvoteSnippet = (snippetId) => {
  const snippet = snippets$[snippetId].get();
  if (!snippet) return;

  const wasDownvoted = snippet.downvotes > 0;
  snippets$[snippetId].downvotes.set(wasDownvoted ? 0 : 1);
  if (!wasDownvoted) {
    snippets$[snippetId].upvotes.set(0);
    trackEvent('snippet_voted', { direction: 'down', snippet_id: snippetId });
  }
};

// Bulk delete snippets for a note
// Note: Pass selectedIds and callbacks to avoid circular dependency
export const bulkDeleteSnippets = (selectedIds, updateNoteTimestamp, clearSelection) => {
  selectedIds.forEach(snippetId => {
    deleteSnippet(snippetId, updateNoteTimestamp);
  });

  if (clearSelection) {
    clearSelection();
  }
};

